/**
 * RightSidebar – Library of images from characters, locations, and storyboard shots, plus completed
 * shot visuals (videos). See ./libraryItems.ts for chip data sourcing and ./useLibraryData.ts for
 * filtering/search/highlight state. Active scope uses the context bar back control (not a chip).
 * Default filter is Active when switching panels.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Box, Divider, IconButton, Paper, Tab, Tabs, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useQueryClient } from "@tanstack/react-query";
import CloseIcon from "@mui/icons-material/Close";
import VideoPlayDialog from "@/components/Media/VideoPlayDialog";
import DetailsTabPanel from "@/components/ProjectPage/DetailsTabPanel";
import { observer } from "mobx-react-lite";
import { useParams } from "react-router-dom";
import { useStore } from "@/contexts/StoreContext";
import { APP_ACTIVE_TOOL_COLOR } from "@/theme";
import { ProjectPanelRoute, type LibraryMediaHighlight } from "@/types/routes";
import { useUpdateProject } from "@/api/api";
import { useOptimisticProjectSave } from "@/hooks/useOptimisticProjectSave";
import { useProjectPermissions } from "@/hooks/useProjectPermissions";
import { isFrameOrVideoPanel, libraryDragRestrictionForPanel } from "@/utils/dragTransfer";
import { storyboardShotMainVideoId } from "@/services/storyboard";
import { patchProjectFavoriteMedia, type FavoriteMediaKind } from "@/services/project-entity";
import { SIDE_PANEL_COLLAPSE_TRANSITION } from "@/utils/constants";
import LibraryFilterBar from "./LibraryFilterBar";
import LibraryGrid from "./LibraryGrid";
import ScriptTabPanel from "./ScriptTabPanel";
import { useLibraryData } from "./useLibraryData";

type SidebarTab = "details" | "library" | "script";

interface RightSidebarProps {
  readonly headerHeight: number;
  readonly width: number;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly libraryHighlight?: LibraryMediaHighlight | null;
  readonly onLibraryHighlightConsumed?: () => void;
}

const RightSidebar = observer(({ headerHeight, width, open, onClose, libraryHighlight = null, onLibraryHighlightConsumed }: RightSidebarProps) => {
  const { actionStore } = useStore();
  const { projectDetails, activeStoryboardShotIndex: activeShotIndex, activeCharacterIndex, activeLocationIndex } = actionStore;
  const { canWrite } = useProjectPermissions(projectDetails);
  const project = projectDetails?.project ?? null;
  const projectId = projectDetails?.project_id ?? null;
  const { panel } = useParams<{ projectId: string; panel: string; entityId?: string }>();
  const updateProjectMutation = useUpdateProject();
  const queryClient = useQueryClient();
  const { commitOptimisticProjectSave } = useOptimisticProjectSave(projectId, updateProjectMutation, actionStore, queryClient);
  const [sidebarTab, setSidebarTab] = useState<SidebarTab>("library");
  const [searchOpen, setSearchOpen] = useState(false);
  const [draggingImageId, setDraggingImageId] = useState<string | null>(null);
  const [draggingLibraryVideoId, setDraggingLibraryVideoId] = useState<string | null>(null);
  const libraryDragContextRef = useRef<{ readonly key: string; readonly sameAsMainspace: boolean } | null>(null);
  const [videoDialogVideoId, setVideoDialogVideoId] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchWrapperRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();

  const handleLibraryDragStart = useCallback((imageId: string, libraryVideoId: string | null) => {
    setDraggingImageId(imageId);
    setDraggingLibraryVideoId(libraryVideoId);
  }, []);

  const handleLibraryDragEnd = useCallback(() => {
    setDraggingImageId(null);
    setDraggingLibraryVideoId(null);
  }, []);

  const charactersList = project?.characters ?? [];
  const safeCharacterIdx = Math.min(activeCharacterIndex, Math.max(0, charactersList.length > 0 ? charactersList.length - 1 : 0));

  const locationsList = project?.locations ?? [];
  const safeLocationIdx = Math.min(activeLocationIndex, Math.max(0, locationsList.length > 0 ? locationsList.length - 1 : 0));

  const currentMainspaceImageId = useMemo((): string | null => {
    if (panel === ProjectPanelRoute.Characters) {
      return charactersList[safeCharacterIdx]?.images?.[0] ?? null;
    }

    if (panel === ProjectPanelRoute.Locations) {
      return locationsList[safeLocationIdx]?.images?.[0] ?? null;
    }

    return project?.["storyboard-shots"]?.[activeShotIndex]?.images?.[0] ?? null;
  }, [panel, charactersList, safeCharacterIdx, locationsList, safeLocationIdx, project, activeShotIndex]);

  const currentMainspaceVideoId = useMemo((): string | null => {
    if (panel === ProjectPanelRoute.Characters || panel === ProjectPanelRoute.Locations || panel === ProjectPanelRoute.ImageFrame) {
      return null;
    }

    const shot = project?.["storyboard-shots"]?.[activeShotIndex];
    if (shot === undefined || shot === null) {
      return null;
    }

    return storyboardShotMainVideoId(shot);
  }, [panel, project, activeShotIndex]);

  useEffect(() => {
    if (draggingImageId === null) {
      libraryDragContextRef.current = null;
      actionStore.setLibraryImageDragging(false);
      return;
    }

    const dragKey = `${draggingImageId}\0${draggingLibraryVideoId ?? ""}`;
    if (libraryDragContextRef.current?.key !== dragKey) {
      const imageMatch = currentMainspaceImageId !== null && draggingImageId === currentMainspaceImageId;
      const videoMatch = (draggingLibraryVideoId ?? "") === (currentMainspaceVideoId ?? "");
      libraryDragContextRef.current = { key: dragKey, sameAsMainspace: imageMatch && videoMatch };
    }

    actionStore.setLibraryImageDragging(true, libraryDragContextRef.current.sameAsMainspace, draggingLibraryVideoId);

    return () => {
      actionStore.setLibraryImageDragging(false);
    };
  }, [actionStore, draggingImageId, draggingLibraryVideoId]);

  useEffect(() => {
    if (searchOpen) {
      searchInputRef.current?.focus();
    }
  }, [searchOpen]);

  const handleSearchBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    const next = e.relatedTarget as Node | null;
    if (next === null || !searchWrapperRef.current?.contains(next)) {
      setSearchOpen(false);
    }
  }, []);

  const {
    sectionFilter,
    setSectionFilter,
    searchQuery,
    setSearchQuery,
    approvedForProjectOnly,
    setApprovedForProjectOnly,
    favoritesOnly,
    setFavoritesOnly,
    displayEntries,
    librarySearchSuggestions,
    completedVideoByImageId,
    highlightedLibraryItemId,
    highlightCardRef,
    viewAllMediaLoading,
    viewAllMediaError,
    viewAllHasMore,
    onLoadMoreViewAll,
    contextBarDescription,
    activeShotBackTooltip
  } = useLibraryData({
    project,
    projectId,
    panel,
    activeShotIndex,
    charactersList,
    safeCharacterIdx,
    locationsList,
    safeLocationIdx,
    libraryHighlight,
    open,
    sidebarTabIsLibrary: sidebarTab === "library",
    onLibraryHighlightMatched: () => setSidebarTab("library"),
    onLibraryHighlightConsumed
  });

  // MainSpace's own drop handling already lets a video card land on Frame (and vice versa on
  // Video) - see MainSpace.tsx's targetPanelForMainSpaceLibraryDrop - but that only matters if the
  // card can be dragged out of the Library in the first place, so this must mirror MainSpace's
  // isFrameOrVideoTab exception rather than applying the strict per-panel restriction here.
  // Timeline slot drops are unaffected: Timeline.tsx checks isLibraryDragAllowedForPanel itself.
  const libraryDragRestriction = isFrameOrVideoPanel(panel) ? null : libraryDragRestrictionForPanel(panel);

  // Shared per-project state (like Approved) - unlike a locked-out double-click, there's no local-preview fallback here.
  const handleToggleFavorite = useCallback(
    (mediaId: string, kind: FavoriteMediaKind) => {
      if (!canWrite) {
        return;
      }

      const current = actionStore.projectDetails;
      if (!projectId || !current?.project) {
        return;
      }

      commitOptimisticProjectSave({ ...current, project: patchProjectFavoriteMedia(current.project, mediaId, kind) });
    },
    [actionStore, canWrite, commitOptimisticProjectSave, projectId]
  );

  const handleAddToMainspace = useCallback(
    (imageId: string, videoId?: string) => {
      if (imageId === currentMainspaceImageId && (videoId ?? "") === (currentMainspaceVideoId ?? "")) {
        return;
      }

      actionStore.mainSpaceLibraryDropHandler?.(imageId, videoId);
    },
    [actionStore, currentMainspaceImageId, currentMainspaceVideoId]
  );

  let sidebarBody: ReactNode = (
    <DetailsTabPanel
      projectId={projectId}
      panel={panel}
      shot={project?.["storyboard-shots"]?.[activeShotIndex]}
      characterPrimaryImageId={charactersList[safeCharacterIdx]?.images?.[0] ?? null}
      locationPrimaryImageId={locationsList[safeLocationIdx]?.images?.[0] ?? null}
      completedStoryboardVideoId={currentMainspaceVideoId}
      onPlayParentVideo={setVideoDialogVideoId}
    />
  );

  if (sidebarTab === "script") {
    sidebarBody = (
      <ScriptTabPanel
        projectId={projectId}
        project={project}
        activeShot={project?.["storyboard-shots"]?.[activeShotIndex]}
        panel={panel}
        active={open}
      />
    );
  } else if (sidebarTab === "library") {
    sidebarBody = (
      <>
        <LibraryFilterBar
          sectionFilter={sectionFilter}
          onSectionFilterChange={setSectionFilter}
          contextBarDescription={contextBarDescription}
          activeShotBackTooltip={activeShotBackTooltip}
          approvedForProjectOnly={approvedForProjectOnly}
          onApprovedForProjectOnlyChange={setApprovedForProjectOnly}
          favoritesOnly={favoritesOnly}
          onFavoritesOnlyChange={setFavoritesOnly}
          searchOpen={searchOpen}
          onSearchOpen={() => setSearchOpen(true)}
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          searchSuggestions={librarySearchSuggestions}
          searchInputRef={searchInputRef}
          searchWrapperRef={searchWrapperRef}
          onSearchBlur={handleSearchBlur}
        />
        <Divider sx={{ marginBottom: 1 }} />
        <LibraryGrid
          projectId={projectId}
          entries={displayEntries}
          isLoading={viewAllMediaLoading}
          isError={viewAllMediaError}
          completedVideoByImageId={completedVideoByImageId}
          libraryDragRestriction={libraryDragRestriction}
          canWrite={canWrite}
          currentMainspaceImageId={currentMainspaceImageId}
          highlightedLibraryItemId={highlightedLibraryItemId}
          highlightCardRef={highlightCardRef}
          draggingImageId={draggingImageId}
          draggingLibraryVideoId={draggingLibraryVideoId}
          onSelect={handleAddToMainspace}
          onDragStart={handleLibraryDragStart}
          onDragEnd={handleLibraryDragEnd}
          onPlayVideo={setVideoDialogVideoId}
          hasMoreToLoad={viewAllHasMore}
          onLoadMore={onLoadMoreViewAll}
          onToggleFavorite={handleToggleFavorite}
        />
      </>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        position: "sticky",
        top: headerHeight,
        height: `calc(100vh - ${headerHeight}px)`,
        width: open ? width : 0,
        flexShrink: 0,
        borderLeftWidth: open ? 1 : 0,
        borderLeftStyle: "solid",
        borderLeftColor: "divider",
        overflow: "hidden",
        transition: SIDE_PANEL_COLLAPSE_TRANSITION,
        background: theme.palette.background.paper
      }}
    >
      {/* Fixed to the fully-open width so content doesn't reflow while the Paper's width animates. */}
      <Box sx={{ width, height: "100%", display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            minHeight: 48,
            ml: 1,
            borderColor: "divider",
            backgroundColor: "background.paper"
          }}
        >
          <Tabs
            value={sidebarTab}
            onChange={(_, value: SidebarTab) => setSidebarTab(value)}
            variant="fullWidth"
            sx={() => ({
              minHeight: 48,
              flex: 1,
              "& .MuiTab-root": { minHeight: 48, fontSize: 13 },
              "& .MuiTab-root.Mui-disabled": { color: theme.palette.action.disabled },
              "& .MuiTab-root.Mui-selected": { color: APP_ACTIVE_TOOL_COLOR },
              "& .MuiTabs-indicator": { backgroundColor: APP_ACTIVE_TOOL_COLOR }
            })}
          >
            <Tab label="Details" value="details" />
            <Tab label="Library" value="library" />
            <Tab label="Screenplay" value="script" />
          </Tabs>
          <Tooltip title="Close" arrow>
            <IconButton size="small" color="inherit" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        {sidebarBody}
        <VideoPlayDialog
          open={videoDialogVideoId !== null}
          projectId={projectId}
          videoId={videoDialogVideoId}
          onClose={() => setVideoDialogVideoId(null)}
        />
      </Box>
    </Paper>
  );
});

export default RightSidebar;
